import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { useContext, useState } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import FabButton from '../common/FabButton'
import QuickBooksConnectButton from '../SystemSetup/QuickBooks/QuickBooksConnectButton'
import AddUnbillableModal from '../Unbillable/AddUnbillableModal'
import EditUnbillableModal from '../Unbillable/EditUnbillableModal'
import EditVisitModal from '../visits/EditVisitModal'
import AddTimeModal from './AddTimeModal'
import TimesheetCustomerLink from './TimesheetCustomerLink'
import TimesheetsCsvExportButton from './TimesheetsCsvExportButton'
import TimesheetsExportBar from './TimesheetsExportBar'
import TimesheetsFilterBar from './TimesheetsFilterBar'
import TimesheetsGrid, { TimesheetsSelection } from './TimesheetsGrid'
import TimesheetsPrintButton from './TimesheetsPrintButton'
import { TimesheetRow } from './timesheetsApi'
import { TimesheetsFilters } from './timesheetsModel'
import { useTimesheetsInvalidator } from './timesheetsQueries'
import './TimePage.css'

const googleMapsApiKey = (window as unknown as { env?: { GOOGLE_API_KEY?: string } }).env?.GOOGLE_API_KEY

type Props = {
  deletedView: boolean
}

const TimePage = ({ deletedView }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [filters, setFilters] = useState<TimesheetsFilters>({})
  const [selection, setSelection] = useState<TimesheetsSelection>({ ids: [], hours: 0 })
  const [showAddTimeModal, setShowAddTimeModal] = useState(false)
  const [showVisitModal, setShowVisitModal] = useState(false)
  const [editVisitId, setEditVisitId] = useState('')
  const [showAddUnbillableModal, setShowAddUnbillableModal] = useState(false)
  const [editUnbillableId, setEditUnbillableId] = useState<string | null>(null)
  const [linkTarget, setLinkTarget] = useState<TimesheetRow | null>(null)
  const invalidateTimesheets = useTimesheetsInvalidator()

  const isAccounting = currentUser?.roles.includes('Accounting') ?? false
  const isConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks ?? false
  const publicFolderUrl = currentUser?.tenant.publicFolderUrl
  const heroImage = publicFolderUrl ? `${publicFolderUrl}/images/default-projects.jpg` : undefined
  const defaultLunchBreakMinutes = currentUser?.tenant.defaultLunchBreak ?? 0

  const handleFiltersChange = (changes: Partial<TimesheetsFilters>) => {
    setFilters(previous => ({ ...previous, ...changes }))
  }

  const closeVisitModal = (changesSaved: boolean) => {
    setShowVisitModal(false)
    if (changesSaved) {
      invalidateTimesheets()
    }
  }

  const closeAddUnbillableModal = (changesSaved: boolean) => {
    setShowAddUnbillableModal(false)
    if (changesSaved) {
      invalidateTimesheets()
    }
  }

  const closeEditUnbillableModal = (changesSaved: boolean) => {
    setEditUnbillableId(null)
    if (changesSaved) {
      invalidateTimesheets()
    }
  }

  return <div className="time-view">
    <div id="main" className="main-time fluid-container">
      <div className="hero-container short">
        <div className="time-page__cover" style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined} />
      </div>

      <div className="hero-details">
        {!deletedView && <h1>
          Timesheets<br />
          <small>Review, edit, and export your timesheets to QuickBooks Online</small>
        </h1>}
        {deletedView && <h1 className="hero-details-deleted-item">
          <span>
            <span className="margin-20">DELETED TIMESHEETS</span>
          </span>
        </h1>}

        <div className="fab-buttons-container">
          {isAccounting && !isConnectedToQuickBooks && <QuickBooksConnectButton />}
          {!deletedView && <>
            <TimesheetsCsvExportButton filters={filters} />
            <TimesheetsPrintButton filters={filters} />
            <FabButton icon={faPlus} title="Add New Visit" onClick={() => setShowAddTimeModal(true)} />
          </>}
        </div>
      </div>

      <section>
        <TimesheetsFilterBar filters={filters} onFiltersChange={handleFiltersChange} />
        <TimesheetsGrid
          deletedView={deletedView}
          filters={filters}
          onSelectionChange={setSelection}
          onEditVisit={visitId => {
            setEditVisitId(visitId)
            setShowVisitModal(true)
          }}
          onEditUnbillable={setEditUnbillableId}
          onCustomerClick={setLinkTarget}
        />
      </section>

      {isAccounting && isConnectedToQuickBooks && <section className="time-page__export">
        <TimesheetsExportBar selection={selection} deletedView={deletedView} />
      </section>}
    </div>

    <AddTimeModal
      isOpen={showAddTimeModal}
      onClose={() => setShowAddTimeModal(false)}
      onAddVisit={() => {
        setShowAddTimeModal(false)
        setEditVisitId('')
        setShowVisitModal(true)
      }}
      onAddUnbillable={() => {
        setShowAddTimeModal(false)
        setShowAddUnbillableModal(true)
      }}
    />

    {showVisitModal && <EditVisitModal
      isOpen={showVisitModal}
      visitId={editVisitId}
      onCloseModal={closeVisitModal}
      googleMapsApiKey={googleMapsApiKey ?? ''}
      defaultLunchBreakMinutes={defaultLunchBreakMinutes}
    />}

    <AddUnbillableModal
      isOpen={showAddUnbillableModal}
      onClose={closeAddUnbillableModal}
    />

    {editUnbillableId && <EditUnbillableModal
      isOpen={true}
      periodId={editUnbillableId}
      onCloseModal={closeEditUnbillableModal}
      googleMapsApiKey={googleMapsApiKey ?? ''}
    />}

    <TimesheetCustomerLink row={linkTarget} onClose={() => setLinkTarget(null)} />
  </div>
}

export default TimePage
